import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft,
  Ticket,
  Calendar,
  MapPin,
  QrCode,
  ChevronRight,
  Download,
  Share2,
  Check,
  Clock,
  X
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const MyTickets = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showPastTickets, setShowPastTickets] = useState(false);

  // Fetch user's tickets
  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ["myTickets", showPastTickets],
    queryFn: async () => {
      const response = await axios.get(`${API}/tickets/my-tickets?upcoming_only=${!showPastTickets}`);
      return response.data;
    }
  });

  const events = ticketsData?.events || [];
  const totalTickets = ticketsData?.total_tickets || 0;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      weekday: "short",
      month: "short", 
      day: "numeric"
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-US", { 
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "valid": return "bg-emerald-500/20 text-emerald-400";
      case "used": return "bg-blue-500/20 text-blue-400";
      case "expired": return "bg-red-500/20 text-red-400";
      case "revoked": return "bg-red-500/20 text-red-400";
      default: return "bg-white/20 text-white";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "valid": return <Ticket className="w-3 h-3" />;
      case "used": return <Check className="w-3 h-3" />;
      case "expired": return <Clock className="w-3 h-3" />;
      case "revoked": return <X className="w-3 h-3" />;
      default: return null;
    }
  };

  // Check for new order highlight
  const newOrderId = location.state?.newOrder;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="my-tickets-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 max-w-lg mx-auto">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/lifestyle")}
            className="text-white hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">My Tickets</h1>
            <p className="text-xs text-muted-foreground">{totalTickets} ticket{totalTickets !== 1 ? "s" : ""}</p>
          </div>
        </div>

        {/* Toggle */}
        <div className="flex bg-white/5 rounded-xl p-1">
          <button
            onClick={() => setShowPastTickets(false)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              !showPastTickets ? "bg-white/10 text-white" : "text-muted-foreground"
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setShowPastTickets(true)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              showPastTickets ? "bg-white/10 text-white" : "text-muted-foreground"
            }`}
          >
            Past
          </button>
        </div>
      </header>

      <main className="px-5 max-w-lg mx-auto">
        {isLoading ? (
          <div className="space-y-4 mt-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16">
            <Ticket className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-white font-medium">No tickets yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              {showPastTickets ? "No past tickets" : "Your purchased tickets will appear here"}
            </p>
            <Button
              onClick={() => navigate("/events")}
              className="mt-4 bg-emerald-500 hover:bg-emerald-600"
            >
              Browse Events
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {events.map((event) => (
              <div 
                key={event.event_id}
                className={`rounded-2xl overflow-hidden border ${
                  newOrderId && event.tickets.some(t => t.order_id === newOrderId)
                    ? "border-emerald-500 bg-emerald-500/5"
                    : "border-white/10 bg-white/5"
                }`}
              >
                {/* Event Header */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-white">{event.event_name}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(event.event_date)}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.venue}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-emerald-400 font-medium">
                      {event.tickets.length} ticket{event.tickets.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                {/* Tickets */}
                <div className="divide-y divide-white/5">
                  {event.tickets.map((ticket) => (
                    <button
                      key={ticket.ticket_id}
                      onClick={() => setSelectedTicket(ticket)}
                      className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors text-left"
                      data-testid={`ticket-${ticket.ticket_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                          <QrCode className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{ticket.tier_name}</p>
                          <p className="text-xs text-muted-foreground">
                            J${ticket.total_paid?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${getStatusColor(ticket.status)}`}>
                          {getStatusIcon(ticket.status)}
                          {ticket.status}
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedTicket(null)}
          />
          <div className="relative w-full max-w-sm mx-4 bg-[#1a1a2e] rounded-3xl overflow-hidden">
            {/* Ticket Header */}
            <div className="p-6 border-b border-white/10 text-center">
              <h2 className="text-lg font-bold text-white">{selectedTicket.event_name}</h2>
              <p className="text-sm text-muted-foreground mt-1">{selectedTicket.tier_name}</p>
            </div>

            {/* QR Code */}
            <div className="p-6 flex flex-col items-center">
              <div className="bg-white p-4 rounded-2xl">
                <QRCodeSVG 
                  value={selectedTicket.qr_code}
                  size={200}
                  level="H"
                  includeMargin={false}
                />
              </div>
              
              <p className="text-xs text-muted-foreground mt-4 text-center font-mono">
                {selectedTicket.ticket_id}
              </p>

              <div className={`mt-3 px-4 py-2 rounded-full ${getStatusColor(selectedTicket.status)}`}>
                <span className="text-sm font-medium flex items-center gap-2">
                  {getStatusIcon(selectedTicket.status)}
                  {selectedTicket.status === "valid" ? "Ready for Entry" : 
                   selectedTicket.status === "used" ? "Already Used" :
                   selectedTicket.status}
                </span>
              </div>
            </div>

            {/* Ticket Details */}
            <div className="px-6 pb-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Date</span>
                <span className="text-white">{formatDate(selectedTicket.event_date)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Time</span>
                <span className="text-white">{formatTime(selectedTicket.event_date)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Venue</span>
                <span className="text-white">{selectedTicket.venue}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Order ID</span>
                <span className="text-white font-mono text-xs">{selectedTicket.order_id}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-white/10 flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-white/20"
                onClick={() => {
                  navigator.share?.({
                    title: `Ticket for ${selectedTicket.event_name}`,
                    text: `My ticket for ${selectedTicket.event_name}`
                  }) || toast.info("Copied to clipboard");
                }}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                onClick={() => setSelectedTicket(null)}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      <NavBar />
    </div>
  );
};

export default MyTickets;
